import { useCallback, useEffect, useState, useRef } from "react";
import { useForm } from "react-hook-form";
import { User } from "$types/User";
import useAuth from "$hooks/useAuth";
import { VerifyUserEmail } from "$services/Coordinators.service";
import { emailRegexp } from "$utils/utils";

export const AT_LEAST_ONE_PHONE_ERROR_MSG =
  "Veuillez saisir au moins un numéro de téléphone";

export const useUserFormLogic = (
  user?: User,
  canHaveTwoPhones = false,
  activitiesOptions?: any[]
) => {
  const { token } = useAuth();
  const [isEmailValid, setIsEmailValid] = useState(true);
  const lastValidatedEmail = useRef<string | null>(null);
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);

  const defaultValues: User = user
    ? {
        ...user,
        lastname: user?.lastname?.toUpperCase(),
      }
    : {
        activity: undefined,
        auth0Id: undefined,
        email: "",
        lastname: "",
        firstname: "",
        isOnlyContact: true,
        isApprover: false,
        phone: "",
      };

  const form = useForm<User>({
    mode: "onChange",
    defaultValues,
  });

  const {
    getValues,
    setValue,
    setError,
    clearErrors,
    formState: { touchedFields },
  } = form;

  const emailValue = form.watch("email");

  const validateEmailExists = useCallback(async () => {
    if (!emailValue || !emailRegexp.test(emailValue)) {
      setIsEmailValid(true);
      clearErrors("email");
      return;
    }

    if (user && user.email === emailValue) {
      setIsEmailValid(true);
      clearErrors("email");
      return;
    }

    // Skip if we already validated this exact email
    if (lastValidatedEmail.current === emailValue) {
      return;
    }

    lastValidatedEmail.current = emailValue;
    const emailExists = await VerifyUserEmail(token, emailValue);

    if (emailExists) {
      setIsEmailValid(false);
      setError("email", {
        type: "manual",
        message: "Cet e-mail existe déjà. Veuillez svp en saisir un nouveau.",
      });
    } else {
      setIsEmailValid(true);
      clearErrors("email");
    }
  }, [emailValue, user, token, setError, clearErrors]);

  // Debounce email validation with proper cleanup
  useEffect(() => {
    // Clear previous timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // Set new timer
    debounceTimer.current = setTimeout(() => {
      validateEmailExists();
    }, 800);

    // Cleanup on unmount or email change
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailValue]);

  const atLeastOnePhone = useCallback(() => {
    return !!getValues("phone") || !!getValues("phone2");
  }, [getValues]);

  // Trigger error after touching the phone fields, even if the field is kept empty
  useEffect(() => {
    if (
      canHaveTwoPhones &&
      (touchedFields.phone || touchedFields.phone2) &&
      !atLeastOnePhone()
    ) {
      setError("phone", {
        message: AT_LEAST_ONE_PHONE_ERROR_MSG,
      });
    }
  }, [
    touchedFields.phone,
    touchedFields.phone2,
    setError,
    atLeastOnePhone,
    canHaveTwoPhones,
  ]);

  // Set default activity if only one option is available
  useEffect(() => {
    if (activitiesOptions?.length === 1) {
      setValue("activity", activitiesOptions[0].value);
    }
  }, [activitiesOptions, setValue]);

  return {
    form,
    atLeastOnePhone,
    isEmailValid,
    validateEmailExists,
  };
};
